/**
 * ClubHotspotPhotos - Manage venue photos for a club's claimed hotspot.
 * Shown in Club Dashboard → Club Info. Lets clubs add their own photos and
 * remove/keep default (enrichment) photos when they've claimed an existing venue.
 */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { getColors } from '../styles/theme';
import { getHotspotByClaimedBy, updateHotspot, ensureClubVenue } from '../services/database';
import { getHotspotPhotoUrl, uploadFile, deleteFile, STORAGE_BUCKETS } from '../services/storage';

const ClubHotspotPhotos = ({ clubId }) => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const colors = getColors(isDarkMode);
  const [hotspot, setHotspot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removingIndex, setRemovingIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clubId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getHotspotByClaimedBy(clubId);
        if (!cancelled) setHotspot(data);
      } catch (e) {
        console.error('ClubHotspotPhotos load:', e);
        if (!cancelled) showToast('Failed to load venue photos', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clubId]);

  const displayPhotos = (hotspot?.photos || []).map((photo) => {
    const isUrl = photo && String(photo).startsWith('http');
    const displayUrl = isUrl ? photo : getHotspotPhotoUrl(photo);
    const isDefault = isUrl && !String(photo).includes('supabase'); // enrichment/default from app
    return { raw: photo, displayUrl, isDefault };
  });

  const handleAddPhoto = async () => {
    if (uploading) return;
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          showToast('Permission to access photos was denied', 'error');
          return;
        }
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.8,
      });
      const cancelled = result.cancelled !== undefined ? result.cancelled : result.canceled;
      if (cancelled) return;

      const assets = (result.assets || result.selected || []).filter((a) => a.uri);
      if (assets.length === 0) {
        showToast('No images selected', 'warning');
        return;
      }

      setUploading(true);
      // If no venue yet, create one from club profile (approved clubs only)
      let targetHotspot = hotspot;
      if (!targetHotspot && clubId) {
        showToast('Setting up your venue...', 'info');
        targetHotspot = await ensureClubVenue(clubId);
        setHotspot(targetHotspot);
      }
      if (!targetHotspot) {
        showToast('Could not create venue. Make sure your club is approved.', 'error');
        setUploading(false);
        return;
      }

      showToast(`Uploading ${assets.length} photo(s)...`, 'info');
      const currentPhotos = targetHotspot.photos || [];
      const newKeys = [];

      for (const asset of assets) {
        const res = await fetch(asset.uri);
        const blob = await res.blob();
        const key = `${targetHotspot.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}.jpg`;
        const { error } = await uploadFile(STORAGE_BUCKETS.HOTSPOT_PHOTOS, key, blob, { contentType: 'image/jpeg' });
        if (error) throw error;
        newKeys.push(key);
      }

      const updatedPhotos = [...currentPhotos, ...newKeys];
      await updateHotspot(targetHotspot.id, { photos: updatedPhotos });
      setHotspot({ ...targetHotspot, photos: updatedPhotos });
      showToast(`${newKeys.length} photo(s) added`, 'success');
    } catch (e) {
      console.error('Add venue photo:', e);
      showToast(e?.message || 'Failed to add photos', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (index) => {
    if (!hotspot || removingIndex !== null) return;
    const current = hotspot.photos || [];
    const raw = current[index];
    const isStorageKey = raw && !String(raw).startsWith('http');

    const confirm = () => {
      setRemovingIndex(index);
      const updated = current.filter((_, i) => i !== index);
      updateHotspot(hotspot.id, { photos: updated })
        .then(() => {
          setHotspot({ ...hotspot, photos: updated });
          showToast('Photo removed', 'success');
          if (isStorageKey) {
            deleteFile(STORAGE_BUCKETS.HOTSPOT_PHOTOS, raw).catch(() => {});
          }
        })
        .catch((e) => {
          showToast(e?.message || 'Failed to remove photo', 'error');
        })
        .finally(() => setRemovingIndex(null));
    };

    if (Platform.OS === 'web') {
      if (window.confirm('Remove this photo from your venue?')) confirm();
    } else {
      Alert.alert('Remove photo', 'Remove this photo from your venue?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Remove', style: 'destructive', onPress: confirm },
      ]);
    }
  };

  if (loading) {
    return (
      <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <View style={styles.sectionHeader}>
          <Icon name="photo-library" size={24} color={colors.textSecondary} />
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Venue photos</Text>
        </View>
        <ActivityIndicator size="small" color={colors.primary} style={{ paddingVertical: 24 }} />
      </View>
    );
  }

  if (!hotspot) {
    return (
      <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <View style={styles.sectionHeader}>
          <Icon name="photo-library" size={24} color={colors.textSecondary} />
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Venue photos</Text>
        </View>
        <Text style={[styles.hint, { color: colors.textSecondary }]}>
          Add photos for your venue. They'll appear on the map when players tap your club. Adding your first photo will set up your venue.
        </Text>
        <TouchableOpacity
          style={[styles.addPhotoBtn, styles.addPhotoBtnStandalone, { backgroundColor: colors.background, borderColor: colors.border }]}
          onPress={handleAddPhoto}
          disabled={uploading}
        >
          {uploading ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <>
              <Icon name="add-a-photo" size={28} color={colors.primary} />
              <Text style={[styles.addPhotoText, { color: colors.text }]}>Add photos</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.section, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <View style={styles.sectionHeader}>
        <Icon name="photo-library" size={24} color={colors.textSecondary} />
        <Text style={[styles.sectionTitle, { color: colors.text }]}>Venue photos</Text>
      </View>
      <Text style={[styles.hint, { color: colors.textSecondary }]}>
        These photos appear on your club's map popup. You can keep or remove default photos and add your own.
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photoScroll}>
        {displayPhotos.map(({ displayUrl, isDefault }, index) => (
          <View key={index} style={[styles.photoWrap, { backgroundColor: colors.background }]}>
            <Image source={{ uri: displayUrl }} style={styles.photo} />
            {isDefault && (
              <View style={[styles.badge, { backgroundColor: colors.border }]}>
                <Text style={[styles.badgeText, { color: colors.textSecondary }]}>Default</Text>
              </View>
            )}
            <TouchableOpacity
              style={[styles.removeBtn, { backgroundColor: 'rgba(0,0,0,0.6)' }]}
              onPress={() => handleRemovePhoto(index)}
              disabled={removingIndex !== null}
            >
              {removingIndex === index ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Icon name="close" size={18} color="#fff" />
              )}
            </TouchableOpacity>
          </View>
        ))}
        <TouchableOpacity
          style={[styles.addPhotoBtn, { backgroundColor: colors.background, borderColor: colors.border }]}
          onPress={handleAddPhoto}
          disabled={uploading}
        >
          {uploading ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <>
              <Icon name="add-a-photo" size={28} color={colors.primary} />
              <Text style={[styles.addPhotoText, { color: colors.text }]}>Add photos</Text>
            </>
          )}
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  hint: {
    fontSize: 14,
    marginBottom: 12,
    lineHeight: 20,
  },
  photoScroll: {
    flexGrow: 0,
  },
  photoWrap: {
    width: 120,
    height: 120,
    borderRadius: 10,
    marginRight: 10,
    overflow: 'hidden',
  },
  photo: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  badge: {
    position: 'absolute',
    bottom: 4,
    left: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  removeBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addPhotoBtn: {
    width: 120,
    height: 120,
    borderRadius: 10,
    borderWidth: 2,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addPhotoBtnStandalone: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  addPhotoText: {
    fontSize: 12,
    marginTop: 6,
    fontWeight: '500',
  },
});

export default ClubHotspotPhotos;
